/**
 * NEEDS OPTIMIZATIONS IN RENDERING SELECTION OPTIONS
 */
import React, { forwardRef, Ref, useEffect, useRef, useState } from 'react';
import {
  Platform,
  StyleProp,
  StyleSheet,
  TextInput as RNTextInput,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
  ViewStyle,
  Keyboard,
} from 'react-native';

import generateID from '../../utils/generate-id';
import Button from '../atoms/Button';
import Dialog from '../atoms/Dialog';
import Drawer from '../atoms/Drawer';
import Icon from '../atoms/Icon';
import Select from '../atoms/Select';
import TextInput, { TextInputProps } from '../atoms/TextInput';

type Props = Pick<
  TextInputProps,
  | 'accessibilityLabel'
  | 'editable'
  | 'label'
  | 'placeholder'
  | 'onFocus'
  | 'onBlur'
  | 'message'
  | 'state'
  | 'style'
  | 'id'
> & {
  /**
   * Must be in YYYY-MM-DD
   */
  value: string;
  /**
   * Minimum date. Must be in YYYY-MM-DD
   */
  minDate?: string;
  /**
   * Maximum date. Must be in YYYY-MM-DD
   */
  maxDate?: string;
  /**
   * Callback function when the select button is pressed
   */
  onSelect?: (value: string) => void;
  selectButtonText: string;
  /**
   * Show DatePicker inside a Dialog rather than in Drawer
   */
  showAsDialog?: boolean;
  /**
   * Custom style for the Dialog
   */
  dialogStyle?: StyleProp<ViewStyle>;
  /**
   * Custom style for the TextInput
   */
  textInputStyle?: StyleProp<ViewStyle>;
  selectorAccessibilityLabel?: string;
  selectButtonAccessibilityLabel?: string;
};

const VALID_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/; // YYYY-MM-DD
const MIN_DATE_DEFAULT_YEAR_DIFF = 110; // 110 years from today

const generateNumberList = (min: number, max: number) =>
  new Array(max - min + 1).fill(null).map((_, index) => min + index);

const pad = (num: number) => `${num < 10 ? `0${num}` : num}`;

const callOutInvalidDate = (propName: string) => {
  console.error(`${propName} must be in YYYY-MM-DD.`);
};

const DatePicker = forwardRef(
  (props: Props, ref: Ref<TouchableWithoutFeedback>) => {
    const isEditable = props.editable === undefined || !!props.editable;
    const [isOpen, setIsOpen] = useState(false);
    const textInputRef = useRef<RNTextInput | null>(null);

    const [selectedYear, setSelectedYear] = useState(0);
    const [selectedMonth, setSelectedMonth] = useState(0);
    const [selectedDay, setSelectedDay] = useState(0);
    const [numOfDays, setNumOfDays] = useState(30);

    /**
     * Monitor and dynamically change the number of days in the picker in a selected month
     */
    useEffect(() => {
      if (selectedYear && selectedMonth) {
        const date = new Date();

        date.setFullYear(selectedYear);
        date.setMonth(selectedMonth + 1);
        date.setDate(0);

        setNumOfDays(date.getDate());

        if (date.getDate() < selectedDay) {
          setSelectedDay(date.getDate());
        }
      }
    }, [selectedYear, selectedMonth]);

    const [maxDate, setMaxDate] = useState(new Date());
    const [minDate, setMinDate] = useState(() => {
      const date = new Date();

      date.setFullYear(date.getFullYear() - MIN_DATE_DEFAULT_YEAR_DIFF);

      return date;
    });

    useEffect(() => {
      if (isOpen) {
        setupMaxDate();
        setupMinDate();
      }
    }, [isOpen]);

    const onPickerOpen = () => {
      // Hide keyboard to unblock the selection
      // Keyboard API is now supported on react-native-web: https://github.com/necolas/react-native-web/commit/b23a4f55dc1571b1e5e99c7d84aadd034df5177b
      Keyboard.dismiss();

      setupSelectedDate();
    };

    const onPickerClose = () => {
      setSelectedYear(0);
      setSelectedMonth(0);
      setSelectedDay(0);
    };

    const closePicker = () => {
      textInputRef.current?.focus();
      setIsOpen(false);
    };

    const setupSelectedDate = () => {
      const value = props.value;

      let date = new Date();

      if (!value || VALID_DATE_REGEX.test(value)) {
        date = value ? new Date(value) : new Date();

        if (!value) {
          date.setFullYear(date.getFullYear() - 18);
        }
      } else {
        callOutInvalidDate('value');
      }

      setSelectedYear(date.getFullYear());
      setSelectedMonth(date.getMonth());
      setSelectedDay(date.getDate());
    };

    const setupMinDate = () => {
      const date = props.minDate;

      if (date && VALID_DATE_REGEX.test(date)) {
        setMinDate(new Date(date));
      } else {
        date && callOutInvalidDate('minDate');
      }
    };

    const setupMaxDate = () => {
      const date = props.maxDate;

      if (date && VALID_DATE_REGEX.test(date)) {
        setMaxDate(new Date(date));
      } else {
        date && callOutInvalidDate('maxDate');
      }
    };

    const content = (
      <>
        <View style={style.selectWrapper}>
          <Select
            value={`${selectedYear}`}
            onSelect={(value) => setSelectedYear(parseInt(value, 10))}
            options={generateNumberList(
              minDate.getFullYear(),
              maxDate.getFullYear()
            ).map((year) => ({
              label: `${year}`,
              value: `${year}`,
            }))}
            style={style.select}
            id={props.id && `${props.id}-year-select`}
          />
          <Select
            value={`${selectedMonth}`}
            onSelect={(value) => setSelectedMonth(parseInt(value, 10))}
            options={generateNumberList(0, 11).map((month) => ({
              label: pad(month + 1),
              value: `${month}`,
            }))}
            style={style.select}
            id={props.id && `${props.id}-month-select`}
          />
          <Select
            value={`${selectedDay}`}
            onSelect={(value) => setSelectedDay(parseInt(value, 10))}
            options={generateNumberList(1, numOfDays).map((day) => ({
              label: pad(day),
              value: `${day}`,
            }))}
            style={style.select}
            id={props.id && `${props.id}-date-select`}
          />
        </View>
        <View style={style.spacer} />
        <Button
          accessibilityLabel={props.selectButtonAccessibilityLabel}
          title={props.selectButtonText}
          onPress={() => {
            props.onSelect?.(
              `${selectedYear}-${pad(selectedMonth + 1)}-${pad(selectedDay)}`
            );

            closePicker();
          }}
          id={props.id && `${props.id}-select-button`}
        />
      </>
    );

    return (
      <>
        <TouchableWithoutFeedback
          ref={ref}
          disabled={!isEditable}
          onPress={() => setIsOpen(true)}
          /** Generate HTML and Test IDs  */
          {...generateID(props.id)}
        >
          <View style={[style.root, props.style]}>
            <TextInput
              {...props}
              onBlur={(e) => !isOpen && props.onBlur?.(e)}
              value={props.value}
              editable={props.editable}
              id={props.id && `${props.id}-field`}
              style={props.textInputStyle}
            />
            <TouchableOpacity
              disabled={!isEditable}
              onPress={() => setIsOpen(true)}
              style={style.openPickerIcon}
              /** Generate HTML and Test IDs  */
              {...generateID(props.id && `${props.id}-open-button`)}
              /** Platform-specific props */
              {...Platform.select({
                web: {
                  accessibilityRole: 'button',
                },
              })}
            >
              <Icon name="caret-down" size="24" />
            </TouchableOpacity>
          </View>
        </TouchableWithoutFeedback>
        {!props.showAsDialog && (
          <Drawer
            open={isOpen}
            onOpen={onPickerOpen}
            onClose={onPickerClose}
            onBackdropPress={closePicker}
            anchor="bottom"
            style={style.drawer}
            accessibilityLabel={props.selectorAccessibilityLabel}
            id={props.id && `${props.id}-drawer`}
          >
            <View style={style.drawerControls}>
              <TouchableOpacity
                onPress={closePicker}
                /** Generate HTML and Test IDs  */
                {...generateID(props.id && `${props.id}-drawer-close-button`)}
                /** Platform-specific props */
                {...Platform.select({
                  web: {
                    accessibilityRole: 'button',
                  },
                })}
              >
                <Icon name="caret-down" size="24" />
              </TouchableOpacity>
            </View>
            <View style={style.spacer} />
            {content}
          </Drawer>
        )}
        {props.showAsDialog && (
          <Dialog
            open={isOpen}
            onOpen={onPickerOpen}
            onClose={onPickerClose}
            onBackdropPress={closePicker}
            onCloseButtonPress={closePicker}
            title={props.label || ''}
            style={props.dialogStyle}
            accessibilityLabel={props.selectorAccessibilityLabel}
            id={props.id && `${props.id}-dialog`}
          >
            {content}
          </Dialog>
        )}
      </>
    );
  }
);

const style = StyleSheet.create({
  root: {
    position: 'relative',
  },
  openPickerIcon: {
    position: 'absolute',
    top: 24,
    right: 8,
  },
  drawer: {
    borderTopLeftRadius: 32,
    borderTopRightRadius: 32,
    paddingTop: 8,
  },
  spacer: {
    marginBottom: 24,
  },
  drawerControls: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  selectWrapper: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginHorizontal: -4,
  },
  select: {
    width: 96,
    marginHorizontal: 4,
  },
});

export default DatePicker;
